"use client";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  CarouselDots,
  type CarouselApi,
} from "@/components/ui/carousel";
import Image from "next/image";
import { useState, useEffect, startTransition } from "react";
import { cn, formatPrice, truncateText } from "@/lib/utils";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { FieldLabel } from "@/components/ui/field";
import { Button } from "@/components/ui/button";
import {
  ArrowLongLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  TagIcon,
  WrenchIcon,
  InformationCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { CheckIcon, ShoppingCartIcon } from "lucide-react";
import { StarIcon as StarOutline } from "@heroicons/react/24/outline";
import { StarIcon as StarSolid } from "@heroicons/react/24/solid";
import { ShieldCheckIcon as ShieldCheckIconOutline } from "@heroicons/react/24/outline";
import TruckIcon from "@/components/atoms/dynamic-icons/truck";
import PadlockIcon from "@/components/atoms/dynamic-icons/padlock";
import ShieldCheckIcon from "@/components/atoms/dynamic-icons/shield-check";
import FaqComponent from "@/components/molecules/faq-component";
import ExploreRecommendations from "@/components/molecules/explore-recommendations";
import Footer from "@/components/molecules/landing-section-two/footer";
import CTASection from "@/components/molecules/landing-section-two/discover";
import ProductDetailsSkeletonLoader from "@/components/atoms/skeleton-loaders/product-details-skeleton-loader";
import { useRouter, usePathname } from "next/navigation";
import { useCart } from "@/hooks/useCart";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import Link from "next/link";
import { formatSlug } from "@/lib/utils/format-slug";
import ShareMenu from "@/components/molecules/share-menu";
import { QuantityCounter } from "@/components/ui/quantity-counter";
import ProductTabs from "./_components/product-tabs";

export default function ProductDetailsPage({ params }: { params: { id: string } }) {
  console.log(params);
  const router = useRouter();
  const pathname = usePathname();
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [shareUrl, setShareUrl] = useState("");
  const isLoading = false;

  useEffect(() => {
    if (typeof window !== "undefined") {
      startTransition(() => {
        setShareUrl(window.location.href);
      });
    }
  }, [pathname]);

  const { addToCart, isAdding } = useCart();
  const [selectedStyle, setSelectedStyle] = useState<string>("");
  const [selectedSize, setSelectedSize] = useState<string>("");
  const [selectedColor, setSelectedColor] = useState<string>("");

  const styleOptions = ["Modern", "Classic", "Minimalist", "Vintage"];
  const sizeOptions = ["Small", "Medium", "Large", "Extra Large"];
  const colorOptions = ["Black", "White", "Gray", "Brown"];

  const product = {
    id: 1,
    name: "Product 1",
    price: 100000,
    noOfPurchases: 123000,
    category: "Category 1",
    inStock: true,
    stock: 100,
    subCategory: "Sub Category 1",
    brand: "Brand 1",
    vendor: "Vendor 1",
    model: "Model 1",
    color: "Color 1",
    material: "Material 1",
    size: "Size 1",
    images: [
      "https://plus.unsplash.com/premium_photo-1683917067988-1b0323c3b574?q=80&w=1974&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      "https://images.unsplash.com/photo-1615874959474-d609969a20ed?q=80&w=2080&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      "https://images.unsplash.com/photo-1611892440504-42a792e24d32?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OHx8YmVkcm9vbSUyMGludGVyaW9yfGVufDB8fDB8fHww",
      "https://images.unsplash.com/photo-1633948393301-d43e3ec0e5cd?q=80&w=1171&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    ],
    description:
      "Product 1 description Lorem ipsum dolor sit amet consectetur adipisicing elit. Quisquam, quos. Lorem ipsum dolor sit amet consectetur adipisicing elit. Quisquam, quos.",
    rating: 4.5,
    noOfReviews: 100,
    installationSupport: true,
    assetProtection: true,
  };

  useEffect(() => {
    if (!api) {
      return;
    }

    startTransition(() => {
      setCurrent(api.selectedScrollSnap() + 1);
    });

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);

  const goToSlide = (index: number) => {
    api?.scrollTo(index);
  };

  const handleAddToCart = () => {
    if (!selectedStyle) {
      toast.error("Please select a style");
      return;
    }
    if (!selectedSize) {
      toast.error("Please select a size");
      return;
    }
    if (!selectedColor) {
      toast.error("Please select a color");
      return;
    }

    addToCart({
      productId: product.id,
      name: product.name,
      price: product.price,
      quantity: quantity,
      image: product.images[0],
      tags: [selectedStyle, selectedSize, selectedColor],
      variants: {
        style: selectedStyle,
        size: selectedSize,
        color: selectedColor,
      },
      stock: 50,
    });
  };
  // todo: add a function to share the product
  // interaactive tooltip
  // configure metadata

  if (isLoading) {
    return (
      <div className='relative'>
        <ArrowLongLeftIcon
          className='text-icon-title absolute top-2 left-5 z-10 size-6 cursor-pointer md:top-2 md:left-20 xl:left-36'
          onClick={() => router.back()}
        />
        <ProductDetailsSkeletonLoader />;
      </div>
    );
  }

  return (
    <div className='relative w-full'>
      <div className='relative mx-auto w-full max-w-7xl px-3 pt-8 md:px-20 md:pt-12 md:pb-20'>
        <ArrowLongLeftIcon
          className='text-icon-title absolute top-2 left-5 z-10 size-6 cursor-pointer md:top-2 md:left-20 xl:left-36'
          onClick={() => router.back()}
        />
        <div className='flex w-full flex-col gap-8 md:flex-row'>
          <aside className='w-full shrink-0 md:w-[45%]'>
            <div className='xl:min-h-screen'>
              <Carousel className='relative w-full overflow-hidden rounded-xl md:rounded-lg' setApi={setApi}>
                <Badge
                  className={cn(
                    product.inStock ? "bg-[#00C950]" : "bg-red-500",
                    "absolute top-3 left-3 z-10 shadow-lg"
                  )}
                >
                  {product.inStock ? "In Stock" : "Out of Stock"}
                </Badge>
                <div className='absolute top-3 right-3 z-10 flex items-center gap-2'>
                  <div
                    className='bg-custom-amber flex size-10 items-center justify-center rounded-full shadow-lg'
                    title='Asset Protection'
                  >
                    <ShieldCheckIconOutline className='text-custom-amber-text size-5 stroke-[1.8px]' />
                  </div>
                  <ShareMenu url={shareUrl} />
                </div>
                <CarouselContent className='-ml-7 rounded-xl md:rounded-lg'>
                  {product.images.map((image, index) => (
                    <CarouselItem className='' key={index}>
                      <div className='bg-muted relative aspect-square w-full overflow-hidden rounded-xl md:rounded-lg'>
                        <Image
                          src={image}
                          alt={`${product.name} - Image ${index + 1}`}
                          width={500}
                          height={500}
                          className='h-full w-full rounded-xl object-contain md:rounded-lg'
                        />
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselDots className='hidden' />
                <CarouselPrevious
                  variant='ghost'
                  className='disabled:bg-bg-base bg-bg-base absolute top-1/2 left-4 z-10 -translate-y-1/2 disabled:opacity-60'
                  customIcon={<ChevronLeftIcon className='size-4' />}
                />
                <CarouselNext
                  variant='ghost'
                  className='disabled:bg-bg-base bg-bg-base absolute top-1/2 right-4 z-10 -translate-y-1/2 disabled:opacity-60'
                  customIcon={<ChevronRightIcon className='size-4' />}
                />
              </Carousel>

              {/* Thumbnail Navigation */}
              <div className='mt-3 flex gap-4 overflow-x-auto px-2 py-2'>
                {product.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={cn(
                      "relative size-20 shrink-0 overflow-hidden rounded-lg transition-all duration-200 hover:scale-105",
                      current === index + 1
                        ? "ring-link-primary ring-2 ring-offset-2"
                        : "ring-1 ring-gray-200 hover:ring-gray-300"
                    )}
                  >
                    <Image
                      src={image}
                      alt={`${product.name} - Thumbnail ${index + 1}`}
                      width={64}
                      height={64}
                      className='h-full w-full object-cover'
                    />
                    {current === index + 1 && <div className='absolute inset-0 bg-black/10' />}
                  </button>
                ))}
              </div>
              <div className='mt-8 hidden md:block'>
                <ProductTabs description={product.description} noOfReviews={product.noOfReviews} />
              </div>
            </div>
          </aside>
          <main className='flex-1'>
            <div className='divide-border-title/10 h-fit divide-y-[1.5px] md:sticky md:top-28'>
              <div className='space-y-2 pb-4'>
                <div className='flex items-center gap-2'>
                  <div className='flex items-center gap-2'>
                    <TagIcon className='text-link-primary size-5' />
                    <span className='text-text-muted text-sm font-normal'>Category:</span>
                    <Breadcrumb>
                      <BreadcrumbList>
                        <BreadcrumbItem>
                          <BreadcrumbPage className='text-text-title'>{product.category}</BreadcrumbPage>
                        </BreadcrumbItem>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                          <BreadcrumbLink className='text-link-primary'>
                            <Link href={`/category/${formatSlug(product.subCategory)}`}>{product.subCategory}</Link>
                          </BreadcrumbLink>
                        </BreadcrumbItem>
                      </BreadcrumbList>
                    </Breadcrumb>
                  </div>
                </div>
                <h3 className='text-text-title text-2xl font-light uppercase md:text-3xl'>{product.name}</h3>
                <div className='divide-border-title/10 flex items-center space-x-2 divide-x'>
                  <p className='flex items-center gap-1 pr-2'>
                    {Array.from({ length: 5 }).map((_, index) => {
                      const fullStars = Math.floor(product.rating);
                      const hasHalf = product.rating - fullStars >= 0.5;
                      const isFull = index < fullStars;
                      const isHalf = !isFull && index === fullStars && hasHalf;
                      return (
                        <span key={index} className='relative inline-flex'>
                          {/* Background fill star (behind outline) */}
                          <StarSolid
                            className={
                              isFull
                                ? "size-4.5 text-[#FFB900]"
                                : isHalf
                                  ? "size-4.5 text-[#FFB900]/50"
                                  : "size-4.5 text-transparent"
                            }
                          />
                          {/* Outline star on top */}
                          <StarOutline className='absolute inset-0 size-4.5 text-[#FFB900]' />
                        </span>
                      );
                    })}

                    <span className='text-text-title/70 pl-2 text-xs font-normal'>({product.rating})</span>
                  </p>
                  {product.noOfReviews > 0 && <p>{product.noOfReviews} reviews</p>}
                </div>

                <p className='text-text-title/70 block text-sm leading-[160%] font-normal'>
                  {truncateText(product.description)}
                </p>
                {/* todo: make this reusable */}
                {product.installationSupport && (
                  <div className='border-border-title/10 my-4 rounded-lg border'>
                    <Accordion type='single' collapsible>
                      <AccordionItem value='installation' className='border-none'>
                        <AccordionTrigger className='border-border-title/10 cursor-pointer bg-[#F7F7F7] px-4 hover:no-underline data-[state=open]:rounded-b-none data-[state=open]:border-b dark:bg-transparent [&>svg]:hidden'>
                          <div className='flex w-full items-center gap-3'>
                            <div className='flex size-8 shrink-0 items-center justify-center rounded-full bg-[#ECECEC] md:size-10'>
                              <WrenchIcon className='size-4 text-[#364153] md:size-5' />
                            </div>
                            <div className='flex-1 text-left'>
                              <h4 className='text-text-title text-sm font-semibold sm:text-base'>Installation</h4>
                              <p className='text-text-title/70 mt-0.5 text-xs font-normal md:text-sm'>
                                Ensure safe and optimal setup for {formatPrice(8500)}
                              </p>
                            </div>
                            <div className='flex size-6 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-transparent'>
                              <InformationCircleIcon className='size-4 text-[#CDCDCD]' />
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className='p-4'>
                          <div className='space-y-4'>
                            <div>
                              <h5 className='text-text-title mb-2 text-sm font-semibold'>What is</h5>
                              <p className='text-text-title/70 text-sm leading-relaxed'>
                                Our certified technicians will expertly install your new solar panels and/or inverter
                                system for peak performance and safety. This includes:
                              </p>
                            </div>
                            <ul className='text-text-title/70 space-y-2 text-sm leading-relaxed'>
                              <li className='flex items-start gap-2'>
                                <span className='mt-1.5 size-1.5 shrink-0 rounded-full bg-[#364153]' />
                                <span>
                                  Professional mounting and wiring of all equipment (panels, inverter, batteries)
                                </span>
                              </li>
                              <li className='flex items-start gap-2'>
                                <span className='mt-1.5 size-1.5 shrink-0 rounded-full bg-[#364153]' />
                                <span>System testing and commissioning to ensure full functionality</span>
                              </li>
                              <li className='flex items-start gap-2'>
                                <span className='mt-1.5 size-1.5 shrink-0 rounded-full bg-[#364153]' />
                                <span>Guidance and handover pack with manuals and maintenance advice</span>
                              </li>
                              <li className='flex items-start gap-2'>
                                <span className='mt-1.5 size-1.5 shrink-0 rounded-full bg-[#364153]' />
                                <span>Comprehensive site assessment and system design verification</span>
                              </li>
                              <li className='flex items-start gap-2'>
                                <span className='mt-1.5 size-1.5 shrink-0 rounded-full bg-[#364153]' />
                                <span>2-year warranty on the installation work</span>
                              </li>
                            </ul>
                            <div className='border-t border-gray-200 pt-4'>
                              <p className='text-text-title mb-4 text-sm font-semibold'>
                                Would you like to add the Professional Installation Service to your order?
                              </p>
                              {/* todo: add add installation behaviour here */}
                              <div className='flex flex-col gap-3 md:flex-row'>
                                <Button size='sm' className='flex-1 rounded-xs py-2 md:py-0'>
                                  <CheckIcon className='size-4' />
                                  Yes, Add Installation ({formatPrice(8500)})
                                </Button>
                                <div className='flex-1'>
                                  <AccordionTrigger className='py-0 hover:no-underline [&>svg]:hidden'>
                                    <Button variant='outline' size='sm' className='w-full rounded-xs'>
                                      <XMarkIcon className='size-4' />
                                      No, Thanks
                                    </Button>
                                  </AccordionTrigger>
                                </div>
                              </div>
                            </div>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </div>
                )}
                {product.assetProtection && (
                  <div className='border-border-title/10 my-4 rounded-lg border'>
                    <Accordion type='single' collapsible>
                      <AccordionItem value='installation' className='border-none'>
                        <AccordionTrigger className='border-border-title/10 cursor-pointer bg-[#F7F7F7] px-4 hover:no-underline data-[state=open]:rounded-b-none data-[state=open]:border-b dark:bg-transparent [&>svg]:hidden'>
                          <div className='flex w-full items-center gap-3'>
                            <div className='flex size-8 shrink-0 items-center justify-center rounded-full bg-[#ECECEC] md:size-10'>
                              <ShieldCheckIconOutline className='size-4 text-[#364153] md:size-5' />
                            </div>
                            <div className='flex-1 text-left'>
                              <h4 className='text-text-title text-sm font-semibold sm:text-base'>Asset Protection</h4>
                              <p className='text-text-title/70 mt-0.5 text-xs font-normal md:text-sm'>
                                Protect your purchase for {formatPrice(85000)}
                              </p>
                            </div>
                            <div className='flex size-6 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-transparent'>
                              <InformationCircleIcon className='size-4 text-[#CDCDCD]' />
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className='p-4'>
                          <div className='space-y-4'>
                            <div>
                              <h5 className='text-text-title mb-2 text-sm font-semibold'>What is</h5>
                              <p className='text-text-title/70 text-sm leading-relaxed'>
                                Our certified technicians will expertly install your new solar panels and/or inverter
                                system for peak performance and safety. This includes:
                              </p>
                            </div>
                            <ul className='text-text-title/70 space-y-2 text-sm leading-relaxed'>
                              <li className='flex items-start gap-2'>
                                <span className='mt-1.5 size-1.5 shrink-0 rounded-full bg-[#364153]' />
                                <span>
                                  Professional mounting and wiring of all equipment (panels, inverter, batteries)
                                </span>
                              </li>
                              <li className='flex items-start gap-2'>
                                <span className='mt-1.5 size-1.5 shrink-0 rounded-full bg-[#364153]' />
                                <span>System testing and commissioning to ensure full functionality</span>
                              </li>
                              <li className='flex items-start gap-2'>
                                <span className='mt-1.5 size-1.5 shrink-0 rounded-full bg-[#364153]' />
                                <span>Guidance and handover pack with manuals and maintenance advice</span>
                              </li>
                              <li className='flex items-start gap-2'>
                                <span className='mt-1.5 size-1.5 shrink-0 rounded-full bg-[#364153]' />
                                <span>Comprehensive site assessment and system design verification</span>
                              </li>
                              <li className='flex items-start gap-2'>
                                <span className='mt-1.5 size-1.5 shrink-0 rounded-full bg-[#364153]' />
                                <span>2-year warranty on the installation work</span>
                              </li>
                            </ul>
                            <div className='border-t border-gray-200 pt-4'>
                              <p className='text-text-title mb-4 text-sm font-semibold'>
                                Would you like to add the Professional Installation Service to your order?
                              </p>
                              {/* todo: add add installation behaviour here */}
                              <div className='flex flex-col gap-3 md:flex-row'>
                                <Button size='sm' className='flex-1 rounded-xs py-2 md:py-0'>
                                  <CheckIcon className='size-4' />
                                  Yes, Add Installation ({formatPrice(8500)})
                                </Button>
                                <div className='flex-1'>
                                  <AccordionTrigger className='py-0 hover:no-underline [&>svg]:hidden'>
                                    <Button variant='outline' size='sm' className='w-full rounded-xs'>
                                      <XMarkIcon className='size-4' />
                                      No, Thanks
                                    </Button>
                                  </AccordionTrigger>
                                </div>
                              </div>
                            </div>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </div>
                )}
                <p className='text-text-title text-3xl font-medium md:text-4xl'>{formatPrice(product.price)}</p>
              </div>
              <div className='pb-4'>
                <Accordion type='single' collapsible>
                  {/* Style Accordion */}
                  <AccordionItem value='item-1'>
                    <AccordionTrigger className='hover:no-underline'>
                      <h4 className='select-none'>
                        Style:{" "}
                        {selectedStyle && <span className='text-text-title/60 ml-1 font-normal'>{selectedStyle}</span>}
                      </h4>
                    </AccordionTrigger>
                    <AccordionContent>
                      <RadioGroup
                        value={selectedStyle}
                        onValueChange={setSelectedStyle}
                        className='flex w-full flex-wrap px-1 py-2'
                      >
                        {styleOptions.map((style) => (
                          <FieldLabel
                            key={style}
                            htmlFor={`style-${style}`}
                            className='has-data-[state=checked]:ring-border-title border-border-primmary text-text-title/60 cursor-pointer rounded-full border px-4 py-2 font-normal transition-all has-data-[state=checked]:bg-transparent has-data-[state=checked]:ring-1 md:px-5 md:py-3'
                          >
                            {style}
                            <RadioGroupItem id={`style-${style}`} value={style} className='hidden' />
                          </FieldLabel>
                        ))}
                      </RadioGroup>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Size Accordion */}
                  <AccordionItem value='item-2'>
                    <AccordionTrigger className='hover:no-underline'>
                      <h4 className='select-none'>
                        Size:{" "}
                        {selectedSize && <span className='text-text-title/60 ml-1 font-normal'>{selectedSize}</span>}
                      </h4>
                    </AccordionTrigger>
                    <AccordionContent>
                      <RadioGroup
                        value={selectedSize}
                        onValueChange={setSelectedSize}
                        className='flex w-full flex-wrap px-1 py-2'
                      >
                        {sizeOptions.map((size) => (
                          <FieldLabel
                            key={size}
                            htmlFor={`size-${size}`}
                            className='has-data-[state=checked]:ring-border-title border-border-primmary text-text-title/60 cursor-pointer rounded-full border px-4 py-2 font-normal transition-all has-data-[state=checked]:bg-transparent has-data-[state=checked]:ring-1 md:px-5 md:py-3'
                          >
                            {size}
                            <RadioGroupItem id={`size-${size}`} value={size} className='hidden' />
                          </FieldLabel>
                        ))}
                      </RadioGroup>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Color Accordion */}
                  <AccordionItem value='item-3'>
                    <AccordionTrigger className='hover:no-underline'>
                      <h4 className='select-none'>
                        Color:{" "}
                        {selectedColor && <span className='text-text-title/60 ml-1 font-normal'>{selectedColor}</span>}
                      </h4>
                    </AccordionTrigger>
                    <AccordionContent>
                      <RadioGroup
                        value={selectedColor}
                        onValueChange={setSelectedColor}
                        className='flex w-full flex-wrap px-1 py-2'
                      >
                        {colorOptions.map((color) => (
                          <FieldLabel
                            key={color}
                            htmlFor={`color-${color}`}
                            className='has-data-[state=checked]:ring-border-title border-border-primmary text-text-title/60 cursor-pointer rounded-full border px-4 py-2 font-normal transition-all has-data-[state=checked]:bg-transparent has-data-[state=checked]:ring-1 md:px-5 md:py-3'
                          >
                            {color}
                            <RadioGroupItem id={`color-${color}`} value={color} className='hidden' />
                          </FieldLabel>
                        ))}
                      </RadioGroup>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
                <div className='mt-2 flex h-12 items-center gap-4'>
                  <QuantityCounter value={quantity} onChange={setQuantity} min={1} containerClassName='h-full' />
                  <Button
                    onClick={handleAddToCart}
                    disabled={isAdding || !selectedStyle || !selectedSize || !selectedColor}
                    title={`${
                      !selectedStyle
                        ? "Please select a style"
                        : !selectedSize
                          ? "Please select a size"
                          : !selectedColor
                            ? "Please select a color"
                            : isAdding
                              ? "Adding..."
                              : "Add to your Cart"
                    }`}
                    className='h-full flex-1 text-xs font-medium tracking-[1.4px] uppercase md:h-full md:text-sm'
                  >
                    <ShoppingCartIcon className='size-4' />
                    {isAdding ? "Adding..." : "Add to your Cart"}
                  </Button>
                </div>
              </div>
              <div className='flex flex-wrap gap-4 pt-5 pb-2 select-none'>
                <span className='text-text-title/60 text-xs font-normal'>
                  Vendor:
                  <span className='text-text-title ml-1 text-xs'>{product.vendor}</span>
                </span>
                <span className='text-text-title/60 text-xs font-normal'>
                  SKU:
                  <span className='text-text-title ml-1 text-xs'>N/a</span>
                </span>
              </div>
              <div className='flex items-center justify-between py-4 select-none'>
                <div className='flex flex-col items-center gap-2'>
                  <TruckIcon className='text-link-primary size-6 stroke-2' />
                  <span className='text-text-title text-center text-sm font-normal'>Nationwide Shipping</span>
                </div>
                <div className='flex flex-col items-center gap-2'>
                  <PadlockIcon className='text-link-primary size-6 stroke-2' />
                  <span className='text-text-title text-center text-sm font-normal'>Secure Payment</span>
                </div>
                {/* todo: conditionally show this */}
                <div className='flex flex-col items-center gap-2'>
                  <ShieldCheckIcon className='text-link-primary size-6 stroke-2' />
                  <span className='text-text-title text-center text-sm font-normal'>6 months warranty</span>
                </div>
              </div>
            </div>
            <div className='mt-8 block md:hidden'>
              <ProductTabs description={product.description} noOfReviews={product.noOfReviews} />
            </div>
          </main>
        </div>
        <div className='mx-auto mt-10 space-y-2 md:mt-20 md:max-w-5xl md:space-y-3'>
          <div className='text-text-title text-center text-3xl font-semibold uppercase'>FAQS</div>
          <p className='text-text-title/70 text-center text-sm font-medium'>Have a question ? We are here to help.</p>
          <FaqComponent />
        </div>
        <div className='mt-16 w-full'>
          <div className='text-text-title mb-3 text-center text-2xl font-normal md:mb-5 md:text-3xl'>
            You May Also Like Our Other Recommendations
          </div>
          <ExploreRecommendations />
        </div>
        <CTASection />
        <Footer />
      </div>
      {/* Sticky bottom bar - shows selected variants */}
      <div className='bg-bg-title-inv absolute right-0 bottom-0 left-0 hidden w-full py-3 md:block'>
        <div className='mx-auto flex items-center justify-between gap-10 md:max-w-3xl'>
          <div className='flex items-center gap-3'>
            <Image
              src={product.images[0]}
              alt={product.name}
              width={65}
              height={65}
              className='aspect-square rounded-xl object-cover'
            />
            <div className='text-text-title-inv flex flex-col'>
              <p className='my-0 max-w-[100px] truncate py-0 text-sm font-medium'>{product.name}</p>
              <p className='my-0 py-0 text-sm font-medium'>{formatPrice(product.price)}</p>
            </div>
          </div>
          <div className='scrollbar-hide flex flex-1 items-center gap-2 overflow-x-auto whitespace-nowrap'>
            {selectedStyle && (
              <div className='w-fit rounded-xl border border-white/20 py-1 pr-14 pl-3'>
                <p className='text-text-title-inv text-xs font-normal'>Style</p>
                <p className='text-text-title-inv text-sm font-normal'>{selectedStyle}</p>
              </div>
            )}
            {selectedSize && (
              <div className='w-fit rounded-xl border border-white/20 py-1 pr-14 pl-3'>
                <p className='text-text-title-inv text-xs font-normal'>Size</p>
                <p className='text-text-title-inv text-sm font-normal'>{selectedSize}</p>
              </div>
            )}
            {selectedColor && (
              <div className='w-fit rounded-xl border border-white/20 py-1 pr-14 pl-3'>
                <p className='text-text-title-inv text-xs font-normal'>Color</p>
                <p className='text-text-title-inv text-sm font-normal'>{selectedColor}</p>
              </div>
            )}
          </div>
          <Button
            onClick={handleAddToCart}
            disabled={isAdding || !selectedStyle || !selectedSize || !selectedColor}
            className='bg-bg-base text-text-title px-5.5 text-sm font-medium tracking-[1.4px] uppercase'
          >
            {isAdding ? "Adding..." : "Add to cart"}
          </Button>
        </div>
      </div>
    </div>
  );
}
